import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Timer, Zap } from "lucide-react";
import BondCoinPoolStage from "../components/bond-coin-pool/BondCoinPoolStage";
import BondCoinPoolSidePanel from "../components/bond-coin-pool/BondCoinPoolSidePanel";
import BondCoinFlipStage from "../components/bond-coin-flip/BondCoinFlipStage";
import BondCoinFlipSidePanel from "../components/bond-coin-flip/BondCoinFlipSidePanel";
import BondCoinModeTabs from "../components/bond-coin/BondCoinModeTabs";
import BondPlayGameTypeRail from "../components/bond-play/BondPlayGameTypeRail";
import { BOND_PLAY_BLUE_THEME } from "../utils/bondPlayGameTypes";
import BondPlayStageHelpButton from "../components/bond-play/BondPlayStageHelpButton";
import BondCoinInfoModal from "../components/bond-play/BondCoinInfoModal";
import BondGameCelebrationModal from "../components/bond-coin/BondGameCelebrationModal";
import { useCoinPoolDisplayRound } from "../hooks/useCoinPoolDisplayRound";
import { useCoinPoolPolling } from "../hooks/useCoinPoolPolling";
import useBondGameNotify from "../hooks/useBondGameNotify";
import { selectAppIsAuthenticated, selectAppUser, patchAppUser } from "../redux/appAuthSlice";
import {
  coinPoolCancelOrder,
  coinPoolJoinRound,
  fetchCoinPoolConstants,
  fetchCoinPoolCurrentRound,
  selectCoinPoolActionLoading,
  selectCoinPoolConstants,
  selectCoinPoolLoading,
  selectCoinPoolRound,
} from "../redux/coinPoolSlice";
import { initCoinPoolSocket } from "../redux/coinPoolSocket";
import { initCoinFlipSocket } from "../redux/coinFlipSocket";
import {
  fetchCoinFlipConstants,
  fetchCoinFlipLanding,
  selectCoinFlipConstants,
} from "../redux/coinFlipSlice";
import {
  COIN_SIDE_LABEL,
  formatCoinPoolCoin,
  userEntriesForRound,
} from "../utils/coinPoolHelpers";
import "../bond-play.css";
import "../bond-draw.css";
import "../bond-coin.css";
import "../bond-game-celebration.css";

const MODES = [
  {
    id: "pool",
    short: "Pool",
    label: "Pool",
    Icon: Timer,
    status: "live",
  },
  {
    id: "flip",
    short: "Flip",
    label: "Instant investor flip",
    Icon: Zap,
    status: "live",
  },
];

const BondCoin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const user = useSelector(selectAppUser);
  const userId = user?._id;
  const userCoin = Number(user?.COIN || 0);

  const round = useSelector(selectCoinPoolRound);
  const displayRound = useCoinPoolDisplayRound(round);
  const constants = useSelector(selectCoinPoolConstants);
  const flipConstants = useSelector(selectCoinFlipConstants);
  const loading = useSelector(selectCoinPoolLoading);
  const actionLoading = useSelector(selectCoinPoolActionLoading);
  const creditedRef = React.useRef(null);

  const [modeId, setModeId] = useState("pool");
  const [coinInfoOpen, setCoinInfoOpen] = useState(false);
  const { toast, celebration, notify, clearToast, clearCelebration } = useBondGameNotify();

  useEffect(() => {
    if (!flipConstants) dispatch(fetchCoinFlipConstants());
  }, [dispatch, flipConstants]);

  useEffect(() => {
    if (modeId === "pool") initCoinPoolSocket(dispatch);
    if (modeId === "flip") {
      initCoinFlipSocket(dispatch);
      dispatch(fetchCoinFlipLanding());
    }
  }, [dispatch, modeId]);

  useCoinPoolPolling(round, modeId === "pool");

  useEffect(() => {
    if (modeId !== "pool") return;
    if (!constants) dispatch(fetchCoinPoolConstants());
    dispatch(fetchCoinPoolCurrentRound());
  }, [dispatch, modeId, constants]);

  useEffect(() => {
    if (modeId !== "pool") return;
    const myEntries = userEntriesForRound(round, userId);
    if (!["complete", "canceled"].includes(round?.status) || myEntries.length === 0) return;
    if (creditedRef.current === round.id) return;
    creditedRef.current = round.id;
    const received = myEntries.reduce((sum, entry) => sum + Number(entry.payout || 0), 0);
    if (received > 0) {
      dispatch(patchAppUser({ COIN: Math.max(0, userCoin + received) }));
    }
  }, [dispatch, modeId, round, userCoin, userId]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(clearToast, 3200);
    return () => window.clearTimeout(id);
  }, [toast, clearToast]);

  const handleCancelOrder = async (orderId) => {
    try {
      const before = displayRound?.myOrders?.find((o) => o.id === orderId);
      const refund = Math.floor(Number(before?.remainingAmount) || 0);
      await dispatch(coinPoolCancelOrder({ orderId })).unwrap();
      if (refund > 0) {
        dispatch(patchAppUser({ COIN: userCoin + refund }));
      }
      notify(
        refund > 0
          ? {
              variant: "refund",
              game: "pool",
              title: "Order cancelled",
              subtitle: "Reserved COIN returned to your balance.",
              amount: refund,
              autoCloseMs: 3500,
            }
          : {
              variant: "info",
              game: "pool",
              title: "Order cancelled",
              subtitle: "Your pool order was removed.",
              autoCloseMs: 3000,
            }
      );
    } catch (error) {
      notify(typeof error === "string" ? error : "Could not cancel order.");
    }
  };

  return (
    <div className="bcoin-page" style={BOND_PLAY_BLUE_THEME}>
      <div className="bcoin-bg-glow" aria-hidden />

      <BondCoinModeTabs modes={MODES} modeId={modeId} onChange={setModeId} />

      <div className="bcoin-layout">
        <BondPlayGameTypeRail activeGameId="bond-coin" />

        <main className="bplay-wheel-stage bcoin-stage-shell">
          <BondPlayStageHelpButton
            helpLabel={modeId === "pool" ? "How Coin Pool works" : "How Coin Flip works"}
            onOpenInfo={() => setCoinInfoOpen(true)}
          />
          {modeId === "pool" ? (
            <BondCoinPoolStage
              round={displayRound}
              constants={constants}
              loading={loading}
              actionLoading={actionLoading}
              isAuthenticated={isAuthenticated}
              userCoin={userCoin}
              onSignIn={() => navigate("/app/login")}
              onPlaceOrder={async ({ side, amount, fillMode, joinIdempotencyKey }) => {
                try {
                  const result = await dispatch(
                    coinPoolJoinRound({ side, amount, fillMode, joinIdempotencyKey })
                  ).unwrap();
                  dispatch(patchAppUser({ COIN: Math.max(0, userCoin - amount) }));
                  const order = result?.order;
                  const filled = Math.floor(Number(order?.filledAmount) || 0);
                  const remaining = Math.floor(Number(order?.remainingAmount ?? amount - filled) || 0);
                  let subtitle = `${formatCoinPoolCoin(amount)} COIN reserved on ${COIN_SIDE_LABEL[side]}.`;
                  if (filled > 0 && remaining > 0) {
                    subtitle = `${formatCoinPoolCoin(filled)} matched · ${formatCoinPoolCoin(remaining)} still waiting.`;
                  } else if (filled >= amount) {
                    subtitle = `Fully matched — your COIN is live in the pool.`;
                  } else if (remaining === amount) {
                    subtitle += " Waiting for an opposite order.";
                  }
                  notify({
                    variant: "ticket",
                    game: "pool",
                    title: filled >= amount ? "Order matched!" : "Order placed",
                    subtitle,
                    pick: COIN_SIDE_LABEL[side],
                    stake: amount,
                    autoCloseMs: 4000,
                  });
                  return true;
                } catch (error) {
                  notify(typeof error === "string" ? error : "Could not place order.");
                  return false;
                }
              }}
            />
          ) : (
            <BondCoinFlipStage
              isAuthenticated={isAuthenticated}
              userCoin={userCoin}
              onSignIn={() => navigate("/app/login")}
              onNotify={notify}
            />
          )}
        </main>

        {modeId === "pool" ? (
          <BondCoinPoolSidePanel
            round={displayRound}
            userId={userId}
            actionLoading={actionLoading}
            onCancelOrder={handleCancelOrder}
          />
        ) : (
          <BondCoinFlipSidePanel userCoin={userCoin} onNotify={notify} />
        )}
      </div>

      {toast ? <div className="bplay-toast">{toast}</div> : null}

      <BondGameCelebrationModal
        open={!!celebration}
        {...celebration}
        onClose={clearCelebration}
      />

      <BondCoinInfoModal
        open={coinInfoOpen}
        onClose={() => setCoinInfoOpen(false)}
        mode={modeId}
        poolConstants={constants}
        flipConstants={flipConstants}
      />
    </div>
  );
};

export default BondCoin;
